import moment, { type Moment } from 'moment-timezone';

import { clampRangeToSpan } from '@src/components/RelativeAbsolutePeriodFilter';
import { clampCalendarRangeToToday, DateUtil } from '@src/packages/utils/dates';
import type { ExecutionsHistoryRange } from '@src/models/interfaces/executionsHistoryDashboard.interface';

import type { TasksChartFilters } from '../tasksFilterConfig';
import { limitToMaxSpan, TASKS_REPORT_MAX_SPAN } from '../tasksFilterConfig';

const DAY_FORMAT = 'YYYY-MM-DD';

/** Returns the concrete inclusive calendar range used by all task-report surfaces. */
export const getTasksReportPeriodRange = (
  filters: TasksChartFilters,
  timeZone = DateUtil.getDefaultTimeZone(),
  now: Moment = moment(),
): [Moment, Moment] => {
  const resolveCalendarDay = (date: Moment) => moment.tz(date.format(DAY_FORMAT), DAY_FORMAT, timeZone);

  if (!filters.relative.isRelative) {
    const range: [Moment, Moment] = [
      resolveCalendarDay(filters.dateRange[0]).startOf(filters.relative.dateType),
      resolveCalendarDay(filters.dateRange[1]).endOf(filters.relative.dateType),
    ];
    return clampRangeToSpan(clampCalendarRangeToToday(range, timeZone, now), filters.relative.dateType, TASKS_REPORT_MAX_SPAN);
  }

  const end = moment.tz(now, timeZone).endOf('day');
  const start = end
    .clone()
    .subtract(filters.relative.relativeAmount, filters.relative.dateType)
    .startOf('day');

  return clampRangeToSpan([start, end], 'day', TASKS_REPORT_MAX_SPAN);
};

/** Calendar-day bounds shared by dashboard and execution-history requests. */
export const getTasksReportCalendarRange = (
  filters: TasksChartFilters,
  timeZone?: string,
  now?: Moment,
): ExecutionsHistoryRange => {
  const [start, end] = getTasksReportPeriodRange(limitToMaxSpan(filters), timeZone, now);

  return {
    from: start.format(DAY_FORMAT),
    to: end.format(DAY_FORMAT),
  };
};

/** Converts the shared report calendar range to the epoch-second bounds used by dashboard APIs. */
export const getTasksReportEpochRange = (
  filters: TasksChartFilters,
  timeZone?: string,
  now?: Moment,
): { gte: string; lte: string } => {
  const [start, end] = getTasksReportPeriodRange(filters, timeZone, now);

  return {
    gte: start.format('X'),
    lte: end.format('X'),
  };
};
